import { useState, useEffect } from 'react';
import { Home, School, MapPin, Heart, Save, Sparkles, Armchair, UtensilsCrossed, BedDouble } from 'lucide-react';
import { chatApi } from '../services/api';
import toast from 'react-hot-toast';

const LOCATIONS = [
  {
    key: 'palace_kitchen',
    label: 'Kitchen',
    icon: UtensilsCrossed,
    placeholder: 'e.g. White fridge on the left wall, granite counter in the center with a fruit bowl, gas stove next to the window, round wooden dining table with 4 chairs, a spice rack above the stove...',
    hint: 'Describe your kitchen layout — what\'s where, key objects, colors, textures'
  },
  {
    key: 'palace_living_room',
    label: 'Living Room',
    icon: Armchair,
    placeholder: 'e.g. Brown leather sofa facing a wall-mounted TV, glass coffee table in the center, a bookshelf on the right wall, family photos on the mantelpiece, a window with green curtains...',
    hint: 'Describe furniture, decorations, and their positions'
  },
  {
    key: 'palace_bedroom',
    label: 'Bedroom',
    icon: BedDouble,
    placeholder: 'e.g. Queen bed against the back wall, bedside table with a lamp on the left, wardrobe with a mirror on the right, study desk near the window, ceiling fan above...',
    hint: 'Describe your bedroom — bed, desk, closet, decorations'
  },
  {
    key: 'palace_school_office',
    label: 'School / Office',
    icon: School,
    placeholder: 'e.g. Classroom with 5 rows of wooden desks, teacher\'s desk at the front with a whiteboard behind it, projector on the ceiling, windows on the left side, notice board near the door...',
    hint: 'Describe your classroom, office, or study room'
  },
  {
    key: 'palace_daily_route',
    label: 'Daily Route',
    icon: MapPin,
    placeholder: 'e.g. Start at my front gate, turn right past the big banyan tree, cross the main road at the traffic signal, pass the chai stall, enter the school gate, walk past the playground...',
    hint: 'Describe landmarks on your daily commute or a familiar route'
  },
  {
    key: 'palace_custom',
    label: 'Custom Location',
    icon: Home,
    placeholder: 'e.g. My grandma\'s house: blue gate, tulsi plant in the courtyard, a swing on the porch, old radio in the hall...',
    hint: 'Any other familiar place you\'d like the AI to use'
  }
];

function MemoryProfile() {
  const [profile, setProfile] = useState({
    palace_kitchen: '',
    palace_bedroom: '',
    palace_living_room: '',
    palace_school_office: '',
    palace_daily_route: '',
    palace_custom: '',
    interests: '',
    age_group: '',
    additional_context: ''
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const res = await chatApi.getMemoryProfile();
      if (res.data && Object.keys(res.data).length > 0) {
        setProfile(prev => ({ ...prev, ...res.data }));
      }
    } catch (error) {
      console.error('Failed to load profile:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await chatApi.saveMemoryProfile(profile);
      toast.success('Memory profile saved! The AI will now use your locations.');
    } catch (error) {
      toast.error('Failed to save profile');
    } finally {
      setIsSaving(false);
    }
  };

  const filledCount = LOCATIONS.filter(l => profile[l.key]?.trim()).length;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Sparkles className="w-6 h-6 text-primary-500" />
          My Memory Profile
        </h1>
        <p className="text-gray-600 mt-1">
          Describe your familiar places so the AI builds memory palaces using YOUR actual rooms and landmarks — no more guessing.
        </p>
        {filledCount > 0 && (
          <p className="text-sm text-primary-600 mt-2">{filledCount} of {LOCATIONS.length} locations filled</p>
        )}
      </div>

      {/* Palace Locations */}
      <div className="space-y-4 mb-8">
        <h2 className="text-lg font-semibold text-gray-800">Memory Palace Locations</h2>
        <p className="text-sm text-gray-500">The more detail you give, the more vivid and personal your memory hooks will be.</p>

        {LOCATIONS.map((loc) => {
          const Icon = loc.icon;
          return (
            <div key={loc.key} className="bg-white border border-gray-200 rounded-xl p-4">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                <Icon className="w-4 h-4 text-gray-400" />
                {loc.label}
              </label>
              <p className="text-xs text-gray-400 mb-2">{loc.hint}</p>
              <textarea
                value={profile[loc.key] || ''}
                onChange={(e) => setProfile({ ...profile, [loc.key]: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                placeholder={loc.placeholder}
              />
            </div>
          );
        })}
      </div>

      {/* Personal Context */}
      <div className="space-y-4 mb-8">
        <h2 className="text-lg font-semibold text-gray-800">Personal Context</h2>

        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
            <Heart className="w-4 h-4 text-gray-400" />
            Interests & Hobbies
          </label>
          <p className="text-xs text-gray-400 mb-2">The AI will weave your interests into memory stories to make them more personal</p>
          <textarea
            value={profile.interests || ''}
            onChange={(e) => setProfile({ ...profile, interests: e.target.value })}
            rows={2}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
            placeholder="e.g. Cricket, Marvel movies, cooking, video games, drawing..."
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Age Group</label>
            <select
              value={profile.age_group || ''}
              onChange={(e) => setProfile({ ...profile, age_group: e.target.value })}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="">Select...</option>
              <option value="10-14">10-14 years</option>
              <option value="15-18">15-18 years</option>
              <option value="19-25">19-25 years</option>
              <option value="26-35">26-35 years</option>
              <option value="36+">36+ years</option>
            </select>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Anything else?</label>
            <input
              type="text"
              value={profile.additional_context || ''}
              onChange={(e) => setProfile({ ...profile, additional_context: e.target.value })}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="e.g. I'm a visual learner, I like humor..."
            />
          </div>
        </div>
      </div>

      <button
        onClick={handleSave}
        disabled={isSaving}
        className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-xl hover:bg-primary-700 disabled:opacity-50 font-medium transition-colors"
      >
        <Save className="w-5 h-5" />
        {isSaving ? 'Saving...' : 'Save Memory Profile'}
      </button>
    </div>
  );
}

export default MemoryProfile;
